'use client';

import { useEffect, useRef, useState, FormEvent } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import ScrollAnimate from '../ScrollAnimate';
import { MAX_RESUME_BYTES, RESUME_ACCEPT, resumeExtension } from '@/lib/resume';
import { OPEN_APPLICATION_SLUG, findJob, jobs, roleLabel } from './jobs';

const inputClass =
    'mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[#257676] focus:outline-none focus:ring-1 focus:ring-[#257676] dark:border-slate-700 dark:bg-[#1f2b27] dark:text-slate-100 dark:placeholder-slate-500';

function initialRole(param: string | null): string {
    return param && findJob(param) ? param : OPEN_APPLICATION_SLUG;
}

/**
 * The application form. On /careers the role is a picker driven by the
 * `?role=` query. On a job page `lockedRole` pins it to that posting and the
 * picker is replaced by a hidden field.
 */
export default function ApplicationForm({ lockedRole }: { lockedRole?: string } = {}) {
    const searchParams = useSearchParams();
    const [role, setRole] = useState<string>(() => lockedRole ?? initialRole(searchParams.get('role')));
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const formLoadTime = useRef<number>(Date.now());

    // Apply buttons on the same page change the query without remounting.
    useEffect(() => {
        if (lockedRole) return;
        setRole(initialRole(searchParams.get('role')));
    }, [searchParams, lockedRole]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);

        const form = e.currentTarget;
        const formData = new FormData(form);
        const resume = formData.get('resume');

        if (!(resume instanceof File) || resume.size === 0) {
            setError('Please attach your résumé.');
            return;
        }
        if (!resumeExtension(resume.name)) {
            setError('Please attach your résumé as a PDF or Word document.');
            return;
        }
        if (resume.size > MAX_RESUME_BYTES) {
            setError('Please keep your résumé under 10 MB.');
            return;
        }

        formData.set('formLoadTime', String(formLoadTime.current));
        setIsSubmitting(true);

        try {
            const response = await fetch('/api/careers', { method: 'POST', body: formData });
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                throw new Error('Server returned an invalid response. Please try again.');
            }
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.error || 'Failed to send application');
            }
            setIsSubmitting(false);
            setIsSubmitted(true);
            form.reset();
            form.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (err) {
            console.error('Error submitting application:', err);
            setError(err instanceof Error ? err.message : 'Failed to send application. Please try again.');
            setIsSubmitting(false);
        }
    };

    if (isSubmitted) {
        return (
            <ScrollAnimate delay={300}>
                <div className="mx-auto max-w-xl">
                    <div role="status" className="rounded-2xl border border-[#10b981]/20 bg-gradient-to-br from-[#10b981]/10 to-[#10b981]/5 p-8 text-center dark:border-[#10b981]/30 dark:from-[#10b981]/20 dark:to-transparent">
                        <svg className="mx-auto mb-4 h-16 w-16 text-[#10b981]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h2 className="mb-3 text-2xl font-bold text-slate-900 dark:text-white">Application received</h2>
                        <p className="mb-6 text-slate-600 dark:text-slate-300">
                            Thank you. We read every application and will be in touch if there is a fit.
                        </p>
                        <button
                            onClick={() => {
                                setRole(lockedRole ?? OPEN_APPLICATION_SLUG);
                                setIsSubmitted(false);
                            }}
                            className="rounded-full bg-[#257676] px-6 py-3 font-semibold text-white transition-all duration-300 hover:bg-[#1e5e5e]"
                        >
                            Send another application
                        </button>
                    </div>
                    <ScrollAnimate delay={400}>
                        <div className="mt-12 mb-12 text-center">
                            <p className="mb-2 text-slate-500 dark:text-slate-400">Questions about applying? Email us</p>
                            <a
                                href="mailto:info@esci.io"
                                className="text-lg font-semibold text-[#257676] transition-colors hover:text-[#1e5e5e] dark:text-[#7fd4cd] dark:hover:text-white"
                            >
                                info@esci.io
                            </a>
                        </div>
                    </ScrollAnimate>
                </div>
            </ScrollAnimate>
        );
    }

    return (
        <ScrollAnimate delay={300}>
            <div className="mx-auto max-w-xl">
                <div className="mb-4 text-center">
                    <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">
                        {lockedRole ? `Apply for ${roleLabel(lockedRole)}` : 'Apply'}
                    </h2>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                        {lockedRole
                            ? 'Attach your résumé and tell us anything the résumé does not.'
                            : 'Pick a role, or send an open application and tell us what you would want to work on.'}
                    </p>
                </div>

                <form className="space-y-3" onSubmit={handleSubmit}>
                    {error && (
                        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
                            <p className="text-sm font-medium">{error}</p>
                        </div>
                    )}

                    {lockedRole ? (
                        <input type="hidden" name="role" value={lockedRole} />
                    ) : (
                    <div>
                        <label htmlFor="role" className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                            Role
                        </label>
                        <select
                            id="role"
                            name="role"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            className={inputClass}
                            required
                        >
                            <option value={OPEN_APPLICATION_SLUG}>Open application</option>
                            {jobs.map((job) => (
                                <option key={job.slug} value={job.slug}>
                                    {job.title}
                                </option>
                            ))}
                        </select>
                    </div>
                    )}

                    <div className="grid gap-3 md:grid-cols-2">
                        <div>
                            <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                                First name
                            </label>
                            <input id="firstName" name="firstName" autoComplete="given-name" className={inputClass} required />
                        </div>
                        <div>
                            <label htmlFor="lastName" className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                                Last name
                            </label>
                            <input id="lastName" name="lastName" autoComplete="family-name" className={inputClass} required />
                        </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                                Email
                            </label>
                            <input id="email" type="email" name="email" autoComplete="email" className={inputClass} required />
                        </div>
                        <div>
                            <label htmlFor="link" className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                                LinkedIn or portfolio <span className="font-normal text-slate-500 dark:text-slate-400">(optional)</span>
                            </label>
                            <input id="link" type="url" name="link" placeholder="https://" autoComplete="url" className={inputClass} />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="resume" className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                            Résumé <span className="font-normal text-slate-500 dark:text-slate-400">(PDF or Word, up to 10 MB)</span>
                        </label>
                        <input
                            id="resume"
                            type="file"
                            name="resume"
                            accept={RESUME_ACCEPT}
                            className={`${inputClass} file:mr-3 file:rounded-md file:border-0 file:bg-[#257676] file:px-3 file:py-1 file:text-xs file:font-semibold file:text-white`}
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="message" className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                            Anything you would like us to know <span className="font-normal text-slate-500 dark:text-slate-400">(optional)</span>
                        </label>
                        <textarea
                            id="message"
                            name="message"
                            rows={3}
                            maxLength={4000}
                            placeholder="What you would want to work on, when you could start, or anything the résumé does not say"
                            className={inputClass}
                        />
                    </div>

                    {/* Honeypot field - hidden from users but visible to bots */}
                    <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
                        <label htmlFor="website">Website</label>
                        <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" />
                    </div>

                    <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                        We keep applications for 12 months and use them only to consider you for a role.{' '}
                        <Link href="/privacy" className="underline underline-offset-2 hover:text-[#257676] dark:hover:text-[#7fd4cd]">
                            Privacy policy
                        </Link>
                    </p>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full transform rounded-full bg-[#257676] px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:bg-[#1e5e5e] disabled:transform-none disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isSubmitting ? 'Sending...' : 'Send application'}
                    </button>
                </form>
            </div>
        </ScrollAnimate>
    );
}
